import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { api } from '@/lib/apiClient';
import type { Media, Plan, License, Leader, ChiefGuest, MediaType } from '@/types/public';

type Env<T> = { ok: true; data: T };
const un = <T>(e: Env<T>): T => e.data;

/* ===================================================================== */
/*                              CUSTOMER                                  */
/* ===================================================================== */

export interface AuthUser {
  id: number;
  userId: string;
  fullName: string;
  email: string;
  mobile: string;
  role: 'admin' | 'customer';
  status: string;
  profilePhoto: string | null;
  referralCode: string;
  createdAt: number;
}

export const customerKeys = {
  me: ['me'] as const,
  wallet: ['me', 'wallet'] as const,
  transactions: (page: number, size: number) => ['me', 'txns', page, size] as const,
  bankAccounts: ['me', 'banks'] as const,
  referrals: ['me', 'referrals'] as const,
  tree: ['me', 'tree'] as const,
  notifications: (page: number) => ['me', 'notifs', page] as const,
  cashback: ['me', 'cashback'] as const,
  commissions: ['me', 'commissions'] as const,
};

export function useMe() {
  return useQuery({
    queryKey: customerKeys.me,
    queryFn: async () =>
      un(
        await api.get<Env<{ user: AuthUser; wallet: unknown; plan: unknown; referrer: unknown }>>(
          '/api/users/me',
        ),
      ),
    retry: false,
  });
}

export function useAuthLogin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { identifier: string; password: string }) =>
      un(await api.post<Env<{ user: AuthUser }>>('/api/auth/login', payload)),
    onSuccess: () => qc.invalidateQueries({ queryKey: customerKeys.me }),
  });
}

export function useAuthRegister() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: {
      fullName: string;
      email: string;
      mobile: string;
      password: string;
      planId: number;
      referralCode?: string;
      /** Required whenever `referralCode` is supplied. */
      treePlacement?: 'left' | 'right';
    }) => un(await api.post<Env<{ user: AuthUser }>>('/api/auth/register', payload)),
    onSuccess: () => qc.invalidateQueries({ queryKey: customerKeys.me }),
  });
}

/**
 * Referrer preview shown on the registration screen when a valid referral
 * code is entered. Backed by `GET /api/auth/referrer/:code`.
 */
export interface ReferrerPreview {
  referrer: { id: number; userId: string; fullName: string; referralCode: string };
  leftSubtreeCount: number;
  rightSubtreeCount: number;
  availableSides: Array<'left' | 'right'>;
}

export function useReferrerPreview(code: string) {
  const upper = code.trim().toUpperCase();
  return useQuery({
    queryKey: ['referrer-preview', upper] as const,
    queryFn: async () => un(await api.get<Env<ReferrerPreview>>(`/api/auth/referrer/${upper}`)),
    enabled: upper.length >= 4,
    retry: false,
    staleTime: 30_000,
  });
}

export function useAuthLogout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => api.post('/api/auth/logout'),
    onSuccess: () => qc.clear(),
  });
}

export function useForgotPassword() {
  return useMutation({
    mutationFn: async (payload: { email: string }) =>
      un(await api.post<Env<{ requested: true; devToken?: string }>>('/api/auth/forgot-password', payload)),
  });
}

export function useResetPassword() {
  return useMutation({
    mutationFn: async (payload: { token: string; password: string }) =>
      un(await api.post<Env<{ reset: true }>>('/api/auth/reset-password', payload)),
  });
}

export function useUpdateProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (patch: {
      fullName?: string;
      mobile?: string;
      profilePhoto?: string;
    }) => un(await api.patch<Env<{ user: AuthUser }>>('/api/users/me', patch)),
    onSuccess: () => qc.invalidateQueries({ queryKey: customerKeys.me }),
  });
}

export function useChangePassword() {
  return useMutation({
    mutationFn: async (payload: { currentPassword: string; newPassword: string }) =>
      un(await api.post<Env<{ changed: true }>>('/api/users/me/password', payload)),
  });
}

export function useBankAccounts() {
  return useQuery({
    queryKey: customerKeys.bankAccounts,
    queryFn: async () =>
      un(await api.get<Env<{ items: BankAccount[] }>>('/api/users/me/bank-accounts')),
  });
}

export interface BankAccount {
  id: number;
  accountHolderName: string;
  bankName: string;
  branch: string | null;
  accountNumber: string;
  ifscCode: string;
  upiId: string | null;
}

export function useUpsertBankAccount(id?: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: Omit<BankAccount, 'id'>) =>
      un(
        id
          ? await api.put<Env<{ bankAccount: BankAccount }>>(`/api/users/me/bank-accounts/${id}`, payload)
          : await api.post<Env<{ bankAccount: BankAccount }>>('/api/users/me/bank-accounts', payload),
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: customerKeys.bankAccounts }),
  });
}

export function useDeleteBankAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => api.delete(`/api/users/me/bank-accounts/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: customerKeys.bankAccounts }),
  });
}

export interface WalletBalance {
  availableBalance: number;
  holdBalance: number;
  withdrawnAmount: number;
  totals: {
    cashback: { paid: number; scheduled: number; due: number };
    referralIncome: number;
    matchingIncome: number;
    totalEarnings: number;
  };
  recentTransactions: Transaction[];
}

export function useWallet() {
  return useQuery({
    queryKey: customerKeys.wallet,
    queryFn: async () => un(await api.get<Env<WalletBalance>>('/api/wallet/me')),
  });
}

export function useRequestWithdrawal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { amount: number; remarks?: string }) =>
      un(await api.post<Env<{ transaction: Transaction }>>('/api/wallet/me/withdraw', payload)),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: customerKeys.wallet });
      qc.invalidateQueries({ queryKey: ['me', 'txns'] });
      qc.invalidateQueries({ queryKey: ['me', 'notifs'] });
    },
  });
}

export interface Transaction {
  id: number;
  userId: number;
  type: string;
  amount: number;
  status: string;
  referenceNumber: string;
  remarks: string | null;
  createdAt: string;
}

export function useTransactions(page = 1, pageSize = 20) {
  return useQuery({
    queryKey: customerKeys.transactions(page, pageSize),
    queryFn: async () =>
      un(
        await api.get<Env<{ items: Transaction[]; page: number; pageSize: number }>>(
          '/api/wallet/me/transactions',
          { query: { page, pageSize } },
        ),
      ),
  });
}

export function useMyReferrals() {
  return useQuery({
    queryKey: customerKeys.referrals,
    queryFn: async () =>
      un(
        await api.get<
          Env<{ code: string; items: { id: number; userId: string; fullName: string; joinedAt: number; status: string }[] }>
        >('/api/referrals/me'),
      ),
  });
}

/* --------------------------------- Tree -------------------------------- */

export interface TreeUser {
  id: number;
  userId: string;
  fullName: string;
}
export interface TreeNodeMeta {
  id: number;
  userId: number;
  parentUserId: number | null;
  leftChildId: number | null;
  rightChildId: number | null;
  level: number;
  position: 'root' | 'left' | 'right';
  leftSubtreeCount: number;
  rightSubtreeCount: number;
}
export interface TreeCommissionSummary {
  pairCount: number;
  totalNet: number;
}
export interface TreeChildrenResponse {
  node: TreeNodeMeta | null;
  left: { node: TreeNodeMeta | null; user: TreeUser | null; matching: TreeCommissionSummary; referralEarned: number };
  right: { node: TreeNodeMeta | null; user: TreeUser | null; matching: TreeCommissionSummary; referralEarned: number };
  matching: TreeCommissionSummary;
  referralEarned: number;
}
export interface TreeCountsResponse {
  userId: number;
  leftSubtreeCount: number;
  rightSubtreeCount: number;
  totalSubtreeCount: number;
}

export function useMyTree() {
  return useQuery({
    queryKey: customerKeys.tree,
    queryFn: async () =>
      un(await api.get<Env<TreeChildrenResponse & { counts: TreeCountsResponse }>>('/api/tree/me')),
  });
}

/** Lazy-load children of a specific tree node (for the tree explorer). */
export function useTreeChildren(userId: number | null) {
  return useQuery({
    queryKey: ['tree', 'children', userId] as const,
    queryFn: async () =>
      un(await api.get<Env<TreeChildrenResponse>>(`/api/tree/children/${userId}`)),
    enabled: userId != null && userId > 0,
    staleTime: 60_000, // cached expanded nodes as required by spec
  });
}

export function useTreeCounts(userId: number | null) {
  return useQuery({
    queryKey: ['tree', 'counts', userId] as const,
    queryFn: async () =>
      un(await api.get<Env<TreeCountsResponse>>(`/api/tree/counts/${userId}`)),
    enabled: userId != null && userId > 0,
    staleTime: 60_000,
  });
}

/* ------------------------------- Ledgers ------------------------------- */

export function useMyCashback() {
  return useQuery({
    queryKey: customerKeys.cashback,
    queryFn: async () =>
      un(await api.get<Env<{ items: CashbackScheduleEntry[] }>>('/api/cashback/me')),
  });
}

export interface CashbackScheduleEntry {
  id: number;
  userId: number;
  planId: number;
  monthNumber: number;
  grossAmount: number;
  adminDeduction: number;
  netAmount: number;
  status: 'scheduled' | 'due' | 'paid' | 'cancelled';
  dueDate: string;
  paidDate: string | null;
}

export function useMyCommissions() {
  return useQuery({
    queryKey: customerKeys.commissions,
    queryFn: async () =>
      un(
        await api.get<
          Env<{
            referral: { id: number; netAmount: number; status: string; createdAt: string }[];
            matching: { id: number; netAmount: number; status: string; createdAt: string }[];
          }>
        >('/api/commissions/me'),
      ),
  });
}

export function useNotifications(page = 1) {
  return useQuery({
    queryKey: customerKeys.notifications(page),
    queryFn: async () =>
      un(
        await api.get<Env<{ items: Notification[]; unread: number; page: number; pageSize: number }>>(
          '/api/notifications',
          { query: { page, pageSize: 20 } },
        ),
      ),
  });
}
export interface Notification {
  id: number;
  title: string;
  message: string;
  type: string;
  isRead: boolean;
  createdAt: string;
}

export function useMarkNotificationRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => api.patch(`/api/notifications/${id}/read`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['me', 'notifs'] }),
  });
}
export function useMarkAllRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => api.post('/api/notifications/read-all'),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['me', 'notifs'] }),
  });
}
export function useDeleteNotification() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => api.delete(`/api/notifications/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['me', 'notifs'] }),
  });
}

/* ===================================================================== */
/*                               ADMIN                                    */
/* ===================================================================== */

export const adminKeys = {
  stats: ['admin', 'stats'] as const,
  users: (page: number, size: number, role?: string) => ['admin', 'users', page, size, role] as const,
  user: (id: number) => ['admin', 'users', id] as const,
  plans: ['admin', 'plans'] as const,
  media: ['admin', 'media'] as const,
  leaders: ['admin', 'leaders'] as const,
  chiefGuests: ['admin', 'chief-guests'] as const,
  licenses: ['admin', 'licenses'] as const,
  contactMessages: (page: number) => ['admin', 'contact-messages', page] as const,
  settings: ['admin', 'settings'] as const,
  manualPayments: ['admin', 'manual-payments'] as const,
  auditLogs: (page: number) => ['admin', 'audit', page] as const,
  analytics: ['admin', 'analytics'] as const,
};

export interface AdminTotals {
  totalUsers: number;
  activeUsers: number;
  pendingManualPayments: number;
  unreadMessages: number;
  plansCount: number;
  leaders: number;
  chiefGuests: number;
  licenses: number;
  galleryItems: number;
  pendingCashback: number;
  pendingReferralCommissions: number;
  pendingMatchingCommissions: number;
  pendingWithdrawals: number;
  unreadNotifications: number;
}

export function useAdminStats() {
  return useQuery({
    queryKey: adminKeys.stats,
    queryFn: async () => un(await api.get<Env<{ totals: AdminTotals }>>('/api/admin/stats')),
  });
}

export interface AnalyticsData {
  cashback: {
    monthly: { month: string; count: number; totalGross: number; totalNet: number }[];
    summary: { count: number; totalGross: number; totalNet: number };
    byStatus: { status: string; count: number; total: number }[];
  };
  referral: {
    monthly: { month: string; count: number; totalGross: number; totalNet: number }[];
    summary: { count: number; totalGross: number; totalNet: number };
    byStatus: { status: string; count: number; total: number }[];
  };
  matching: {
    monthly: { month: string; count: number; totalGross: number; totalNet: number }[];
    summary: { count: number; totalGross: number; totalNet: number };
    byStatus: { status: string; count: number; total: number }[];
  };
  plansSold: {
    monthly: { month: string; count: number; totalGross: number; totalNet: number }[];
  };
  userRegistrations: {
    monthly: { month: string; count: number }[];
  };
}

export function useAdminAnalytics() {
  return useQuery({
    queryKey: adminKeys.analytics,
    queryFn: async () => un(await api.get<Env<AnalyticsData>>('/api/admin/analytics')),
  });
}

export function useAdminUsers(page = 1, pageSize = 20, role?: 'admin' | 'customer') {
  return useQuery({
    queryKey: adminKeys.users(page, pageSize, role),
    queryFn: async () =>
      un(
        await api.get<Env<{ items: AuthUser[]; total: number; page: number; pageSize: number }>>(
          '/api/admin/users',
          { query: { page, pageSize, role } },
        ),
      ),
  });
}

export function useAdminUser(id: number | undefined) {
  return useQuery({
    queryKey: id ? adminKeys.user(id) : ['admin', 'users', 'none'],
    queryFn: async () => un(await api.get<Env<unknown>>(`/api/admin/users/${id}`)),
    enabled: Boolean(id),
  });
}

export function useSetUserStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, status }: { id: number; status: string }) =>
      un(await api.patch<Env<{ user: AuthUser }>>(`/api/admin/users/${id}/status`, { status })),
    onSuccess: (_r, v) => {
      qc.invalidateQueries({ queryKey: ['admin', 'users'] });
      qc.invalidateQueries({ queryKey: adminKeys.user(v.id) });
    },
  });
}
export function useAdminResetUserPassword() {
  return useMutation({
    mutationFn: async (id: number) =>
      un(await api.post<Env<{ tempPassword: string }>>(`/api/admin/users/${id}/reset-password`)),
  });
}

export function useUpdateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...fields }: { id: number; fullName?: string; email?: string; mobile?: string }) =>
      un(await api.patch<Env<{ user: AuthUser }>>(`/api/admin/users/${id}`, fields)),
    onSuccess: (_r, v) => {
      qc.invalidateQueries({ queryKey: ['admin', 'users'] });
      qc.invalidateQueries({ queryKey: adminKeys.user(v.id) });
    },
  });
}

export function useAdminPlans() {
  return useQuery({
    queryKey: adminKeys.plans,
    queryFn: async () => un(await api.get<Env<{ items: Plan[] }>>('/api/admin/plans')),
  });
}
export function useUpsertPlan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: Omit<Plan, 'id'> & { id?: number }) => {
      const { id, ...body } = payload;
      return un(
        id
          ? await api.put<Env<{ plan: Plan }>>(`/api/admin/plans/${id}`, body)
          : await api.post<Env<{ plan: Plan }>>('/api/admin/plans', body),
      );
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: adminKeys.plans }),
  });
}
export function useSetPlanStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, status }: { id: number; status: 'active' | 'inactive' }) =>
      api.patch(`/api/admin/plans/${id}/status`, { status }),
    onSuccess: () => qc.invalidateQueries({ queryKey: adminKeys.plans }),
  });
}
export function useDeletePlan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => api.delete(`/api/admin/plans/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: adminKeys.plans }),
    onError: (err) => toast.error((err as Error).message),
  });
}
export function useReorderPlans() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (ids: number[]) => api.patch(`/api/admin/plans/reorder`, { ids }),
    onSuccess: () => qc.invalidateQueries({ queryKey: adminKeys.plans }),
    onError: (err) => toast.error((err as Error).message),
  });
}

export function useAdminMedia() {
  return useQuery({
    queryKey: adminKeys.media,
    queryFn: async () => un(await api.get<Env<{ items: Media[] }>>('/api/admin/media')),
  });
}
export function useCreateMedia() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: {
      type: MediaType;
      title: string;
      fileUrl: string;
      displayOrder?: number;
    }) => un(await api.post<Env<{ media: Media }>>('/api/admin/media', payload)),
    onSuccess: () => qc.invalidateQueries({ queryKey: adminKeys.media }),
  });
}
export function useUploadMedia() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { file: File; type: MediaType; title: string; displayOrder?: number }) => {
      const fd = new FormData();
      fd.append('file', payload.file);
      fd.append('type', payload.type);
      fd.append('title', payload.title);
      if (payload.displayOrder !== undefined) fd.append('displayOrder', String(payload.displayOrder));
      const res = await fetch(`${(import.meta.env.VITE_API_BASE_URL as string).replace(/\/$/, '')}/api/admin/media/upload`, {
        method: 'POST',
        credentials: 'include',
        body: fd,
      });
      if (!res.ok) throw new Error(`Upload failed: ${res.status}`);
      return (await res.json()) as { ok: true; data: { media: Media } };
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: adminKeys.media }),
  });
}
export function useDeleteMedia() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => api.delete(`/api/admin/media/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: adminKeys.media }),
  });
}

export function useAdminLeaders() {
  return useQuery({
    queryKey: adminKeys.leaders,
    queryFn: async () => un(await api.get<Env<{ items: Leader[] }>>('/api/admin/leaders')),
  });
}
export function useUpsertLeader() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: Omit<Leader, 'id' | 'status'> & { status?: string; id?: number }) => {
      const { id, ...body } = payload;
      return un(
        id
          ? await api.put<Env<{ leader: Leader }>>(`/api/admin/leaders/${id}`, body)
          : await api.post<Env<{ leader: Leader }>>('/api/admin/leaders', body),
      );
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: adminKeys.leaders }),
  });
}
export function useDeleteLeader() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => api.delete(`/api/admin/leaders/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: adminKeys.leaders }),
  });
}

export function useAdminChiefGuests() {
  return useQuery({
    queryKey: adminKeys.chiefGuests,
    queryFn: async () => un(await api.get<Env<{ items: ChiefGuest[] }>>('/api/admin/chief-guests')),
  });
}
export function useUpsertChiefGuest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: Omit<ChiefGuest, 'id' | 'status'> & { status?: string; id?: number }) => {
      const { id, ...body } = payload;
      return un(
        id
          ? await api.put<Env<{ chiefGuest: ChiefGuest }>>(`/api/admin/chief-guests/${id}`, body)
          : await api.post<Env<{ chiefGuest: ChiefGuest }>>('/api/admin/chief-guests', body),
      );
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: adminKeys.chiefGuests }),
  });
}
export function useDeleteChiefGuest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => api.delete(`/api/admin/chief-guests/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: adminKeys.chiefGuests }),
  });
}

export function useAdminLicenses() {
  return useQuery({
    queryKey: adminKeys.licenses,
    queryFn: async () =>
      un(await api.get<Env<{ items: License[]; max: number }>>('/api/admin/licenses')),
  });
}
export function useUpsertLicense() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: Omit<License, 'id' | 'createdAt'> & { id?: number }) => {
      const { id, ...body } = payload;
      return un(
        id
          ? await api.put<Env<{ license: License }>>(`/api/admin/licenses/${id}`, body)
          : await api.post<Env<{ license: License }>>('/api/admin/licenses', body),
      );
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: adminKeys.licenses }),
  });
}
export function useDeleteLicense() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => api.delete(`/api/admin/licenses/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: adminKeys.licenses }),
  });
}

export function useContactMessages(page = 1) {
  return useQuery({
    queryKey: adminKeys.contactMessages(page),
    queryFn: async () =>
      un(
        await api.get<
          Env<{
            items: { id: number; name: string; email: string; subject: string; message: string; status: string; createdAt: string }[];
            page: number;
            pageSize: number;
          }>
        >('/api/admin/contact-messages', { query: { page, pageSize: 20 } }),
      ),
  });
}
export function useSetContactStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, status }: { id: number; status: string }) =>
      api.patch(`/api/admin/contact-messages/${id}/status`, { status }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'contact-messages'] }),
  });
}

export function useAdminSettings() {
  return useQuery({
    queryKey: adminKeys.settings,
    queryFn: async () =>
      un(await api.get<Env<{ items: { key: string; value: string }[] }>>('/api/admin/settings')),
  });
}
export function useSaveSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (entries: { key: string; value: string }[]) =>
      un(await api.put<Env<{ items: unknown[] }>>('/api/admin/settings', { entries })),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: adminKeys.settings });
      qc.invalidateQueries({ queryKey: ['public', 'content'] });
    },
  });
}

export function useBroadcast() {
  return useMutation({
    mutationFn: async (payload: {
      target: 'all' | 'role' | 'user';
      role?: string;
      userId?: number;
      title: string;
      message: string;
      type?: string;
    }) => un(await api.post<Env<{ delivered: number }>>('/api/admin/notifications', payload)),
  });
}

export function useManualPayments() {
  return useQuery({
    queryKey: adminKeys.manualPayments,
    queryFn: async () => un(await api.get<Env<{ items: unknown[] }>>('/api/admin/manual-payments')),
  });
}
export function useActionManualPayment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      action,
      remarks,
    }: {
      id: number;
      action: 'approved' | 'rejected' | 'pending';
      remarks?: string;
    }) =>
      un(await api.patch<Env<unknown>>(`/api/admin/manual-payments/${id}`, { action, remarks })),
    onSuccess: () => qc.invalidateQueries({ queryKey: adminKeys.manualPayments }),
  });
}

export function useAuditLogs(page = 1) {
  return useQuery({
    queryKey: adminKeys.auditLogs(page),
    queryFn: async () =>
      un(
        await api.get<
          Env<{ items: { id: number; action: string; entity: string; adminId: number | null; createdAt: string }[]; page: number }>
        >('/api/admin/audit-logs', { query: { page, pageSize: 50 } }),
      ),
  });
}


/* --------------------------- Admin business queues --------------------- */

export interface AdminCashbackQueueEntry {
  entry: {
    id: number;
    userId: number;
    planId: number;
    monthNumber: number;
    grossAmount: number;
    adminDeduction: number;
    netAmount: number;
    status: 'scheduled' | 'due' | 'paid' | 'cancelled';
    dueDate: string;
    paidDate: string | null;
  };
  user: AuthUser | null;
}

export function useAdminCashbackQueue() {
  return useQuery({
    queryKey: ['admin', 'cashback', 'queue'] as const,
    queryFn: async () =>
      un(await api.get<Env<{ items: AdminCashbackQueueEntry[] }>>('/api/admin/cashback/queue')),
  });
}

export function useAdminCashbackSweep() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () =>
      un(await api.post<Env<{ updated: number }>>('/api/admin/cashback/sweep')),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'cashback'] }),
  });
}

export function useAdminPayCashback() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) =>
      un(await api.post<Env<{ entry: AdminCashbackQueueEntry['entry'] }>>(`/api/admin/cashback/${id}/pay`)),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'cashback'] });
      qc.invalidateQueries({ queryKey: ['admin', 'stats'] });
    },
  });
}

export interface AdminCommissionRow {
  id: number;
  referrerId?: number;
  userId?: number;
  referredUserId?: number;
  leftMemberId?: number;
  rightMemberId?: number;
  planId?: number;
  grossAmount: number;
  adminDeduction: number;
  netAmount: number;
  status: 'pending' | 'credited' | 'reversed';
  createdAt: string;
  referrerName?: string;
  referrerUserId?: string;
  referredName?: string;
  referredUserIdStr?: string;
  ownerName?: string;
  leftName?: string;
  rightName?: string;
}

export function useAdminPendingCommissions() {
  return useQuery({
    queryKey: ['admin', 'commissions', 'pending'] as const,
    queryFn: async () =>
      un(
        await api.get<
          Env<{ referral: AdminCommissionRow[]; matching: AdminCommissionRow[] }>
        >('/api/admin/commissions/pending'),
      ),
  });
}

export function useAdminPayReferralCommission() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) =>
      un(await api.post<Env<{ commission: AdminCommissionRow }>>(`/api/admin/commissions/referral/${id}/pay`)),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'commissions'] }),
  });
}

export function useAdminPayMatchingCommission() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) =>
      un(await api.post<Env<{ commission: AdminCommissionRow }>>(`/api/admin/commissions/matching/${id}/pay`)),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'commissions'] }),
  });
}

export interface AdminWithdrawalRow {
  transaction: Transaction;
  user: AuthUser | null;
  bank: { id: number; bankName: string; accountNumber: string; ifscCode: string; accountHolderName: string } | null;
}

export function useAdminWithdrawalQueue() {
  return useQuery({
    queryKey: ['admin', 'withdrawals'] as const,
    queryFn: async () =>
      un(await api.get<Env<{ items: AdminWithdrawalRow[] }>>('/api/admin/withdrawals')),
  });
}

export function useAdminActionWithdrawal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      action,
      remarks,
    }: {
      id: number;
      action: 'approve' | 'reject';
      remarks?: string;
    }) =>
      un(
        await api.patch<Env<{ transaction: Transaction }>>(
          `/api/admin/withdrawals/${id}`,
          { action, remarks },
        ),
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'withdrawals'] }),
  });
}

/* --------------------------- Admin lazy tree explorer ------------------- */
// Admins reuse the same `/api/tree/children/:id` and `/api/tree/counts/:id`
// endpoints. No additional hooks needed — see `useTreeChildren` / `useTreeCounts`.
